import React, { useState, useEffect } from 'react';
import DebugPanel from './debug/DebugPanel.jsx';
import QueryForm from './form/QueryForm.jsx';
import ErrorDisplay from './form/ErrorDisplay.jsx';
import ResponseDisplay from './form/ResponseDisplay.jsx';
import { createMockApi, TEST_SCENARIOS, LANGUAGES, getMockConfig } from '../utils/mockApi';
import { getConfig } from '../config';

// Environment-aware API configuration
const getApiConfig = () => {
  const isDevelopment = process.env.REACT_APP_ENV !== 'production';
  const configs = {
    development: {
      baseUrl: 'http://localhost:3001',
      endpoint: '/api/query',
      timeoutMs: 60000
    },
    production: {
      baseUrl: '', // Empty string for relative path in production
      endpoint: '/api/query',
      timeoutMs: 60000
    }
  };
  
  return isDevelopment ? configs.development : configs.production;
};

const API_CONFIG = getApiConfig();
const DEVELOPMENT_MODE = false;  // Set to true to use mock API
export const VIEW_MODE = true;  // Set to true to hide debug panel

const QueryWorker = () => {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mockConfig] = useState(getMockConfig(TEST_SCENARIOS.EN_STANDARD));
  const [currentLanguage] = useState(LANGUAGES.EN);
  const [debugInfo, setDebugInfo] = useState({
    status: 'Idle',
    courseDataLoaded: {
      status: true,
      filename: 'course_modules__en.json'
    },
    instructionsLoaded: {
      status: true,
      filename: 'instructions.md'
    },
    apiStats: null,
    lastUpdate: null,
    mockApiConfig: mockConfig,
    environment: process.env.REACT_APP_ENV || 'development'
  });

  const mockApi = createMockApi(mockConfig);

  // Helper function to calculate estimated cost based on model type
  const calculateCost = (usage, model) => {
    if (!usage?.input_tokens || !usage?.output_tokens) return '$0.00';
    
    // Cost per million tokens
    const rates = model?.toLowerCase().includes('haiku')
      ? { input: 0.25, output: 1.25 }   // Haiku rates
      : { input: 15, output: 75 };      // Opus rates (default)
    
    const promptCost = (usage.input_tokens / 1000000) * rates.input;
    const completionCost = (usage.output_tokens / 1000000) * rates.output;
    
    return `$${(promptCost + completionCost).toFixed(4)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResponse(null);

    const config = getConfig();
    const url = `${config.apiUrl}${config.apiEndpoint}`;

    try {
      let data;
      
      if (DEVELOPMENT_MODE) {
        // Use mock API in development mode
        data = await mockApi.query(query);
      } else {
        // Use real API
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query }),
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }

        data = await response.json();
      }

      setResponse(data);
      
      // Update debug info with API stats
      if (data.model && data.id) {
        const usage = {
          input_tokens: data.input_tokens,
          output_tokens: data.output_tokens
        };

        setDebugInfo(prev => ({
          ...prev,
          apiStats: {
            idMessage: data.id,
            model: data.model,
            processingTime: data.query_duration,
            timestamp: data.timestamp,
            promptTokens: data.input_tokens,
            completionTokens: data.output_tokens,
            totalTokens: data.input_tokens + data.output_tokens,
            estimatedCost: calculateCost(usage, data.model)            
          }
        }));
      }
      
    } catch (err) {
      console.error('API Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-5">
      <div className="space-y-6">
        {!VIEW_MODE && <DebugPanel debugInfo={debugInfo} />}
        <QueryForm 
          query={query}
          setQuery={setQuery}
          loading={loading}
          handleSubmit={handleSubmit}
        />
        <ErrorDisplay error={error} />
        <ResponseDisplay response={response} />
      </div>
    </div>
  );
};

export default QueryWorker;